import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { HelpDialog } from "@/components/HelpDialog";
import { Loader2, Eye, EyeOff, Bot, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const SUPPORT_EMAIL = "support@velopaths.com";

export default function Login() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  const [helpOpen, setHelpOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setSession(data.token, data.user, remember);
      const home = data.user.role === "admin" ? "/admin" : data.user.role === "shop" ? "/shop" : "/advertiser";
      navigate(home);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const openForgot = () => { setForgotEmail(email); setForgotDone(false); setForgotOpen(true); };
  const sendForgot = async (e) => {
    e.preventDefault();
    setForgotSending(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotDone(true); // generic success — never reveals whether the email exists
    } catch (err) {
      setForgotDone(true);
    } finally {
      setForgotSending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8">
          <div className="bg-white rounded-xl px-4 py-3 inline-flex">
            <img src="/velopaths-logo.png" alt="Velopaths" className="h-9 w-auto" />
          </div>
        </Link>
        <div className="rounded-2xl border border-border bg-card p-8">
          <h1 className="font-heading text-2xl font-bold mb-1">Welcome back</h1>
          <p className="text-sm text-muted-foreground mb-6">Sign in to your Velopaths account.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Input id="password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" className="pr-10" />
                <button type="button" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? "Hide password" : "Show password"} data-testid="login-toggle-password"
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                  <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="login-remember-me" />
                  Remember me
                </label>
                <button type="button" onClick={openForgot} className="text-sm text-primary hover:underline" data-testid="login-forgot-password">Forgot password?</button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Sign in
            </Button>
          </form>

          <button type="button" onClick={() => setHelpOpen(true)} data-testid="login-ask-velo"
            className="mt-4 w-full flex items-center justify-center gap-1.5 text-sm text-primary hover:underline">
            <Bot className="w-4 h-4" />Need help? Ask Velo
          </button>

          <div className="mt-6 text-sm text-center text-muted-foreground space-y-1">
            <div>New Partner? <Link to="/signup/shop" className="text-primary hover:underline">Onboard here</Link></div>
            <div>Business advertiser? <Link to="/signup/advertiser" className="text-primary hover:underline">Sign up</Link></div>
            <div className="pt-2 border-t border-border mt-3">
              Need additional help? <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline" data-testid="login-contact-support">Contact Support</a>
            </div>
          </div>
        </div>
      </div>

      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} onForgotPassword={openForgot} />

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle>Reset your password</DialogTitle>
            <DialogDescription>Enter your account email and we'll send a secure reset link.</DialogDescription>
          </DialogHeader>
          {forgotDone ? (
            <div className="py-4 flex items-start gap-3 text-sm" data-testid="forgot-success">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <p className="text-muted-foreground">If an account exists for <span className="text-foreground font-medium">{forgotEmail}</span>, a password reset link is on its way. The link expires in 45 minutes. Be sure to check your spam folder.</p>
            </div>
          ) : (
            <form onSubmit={sendForgot} className="space-y-4">
              <div>
                <Label htmlFor="forgot-email">Email</Label>
                <Input id="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required data-testid="forgot-email-input" className="mt-1.5" />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setForgotOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={forgotSending} data-testid="forgot-submit">{forgotSending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Send reset link</Button>
              </DialogFooter>
            </form>
          )}
          <div className="text-xs text-muted-foreground flex items-center gap-1.5 pt-2 border-t border-border">
            <Mail className="w-3.5 h-3.5" />Still stuck? <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">Contact Support</a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
